/** @module csi/view/customview/_DefaultControllerMixin */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/action/Controller",
	"csi/comm",
	"csi/locale",
	"csi/middleware/customView/customViewSaveService",
	"csi/prefs",
	"csi/state",
	"csi/widget/MessageDialog",
	"csi/xml",
	"csi/xml/Search",
], function(declare, _, csi, Controller, comm, locale,  { filterOutEmptyGroup, writeCustomView }, csiPrefs, state, MessageDialog, xml, Search) {

	return declare(null, {
		/** @property {Object<string,PreferenceView>} _changedViews - map of custom views that were created or changed */
		_changedViews: null,

		/** @property {Object<string,boolean>} _deletedViewUrls - map of custom view URLs that were deleted */
		_deletedViewUrls: null,

		/** @property {Object<string,boolean>} _newViewUrls - map of new custom view URLs that were created */
		_newViewUrls: null,

		constructor: function() {
			this._changedViews = {};
			this._deletedViewUrls = {};
			this._newViewUrls = {};
		},

		/**
		 * Extension point to override behavior of deleting a custom view and its dependents
		 * @param {boolean} confirmed
		 * @param {string} selectedUrl
		 * @param {string[]} dependentUrls
		 */
		onConfirmDeleteCustomView: function(confirmed, selectedUrl, dependentUrls) { },

		/**
		 * Apply changes to custom view store
		 */
		_applyCustomViewChangesToStore: function() {
			const {widget, _changedViews, _deletedViewUrls} = this;
			const key = widget.getViewPreferenceKey();
			const store = this._getCustomViewStore();
			const modifiedUrls = [];

			_.each(_changedViews, (customView, url) => {
				store.put(customView);
				modifiedUrls.push(url);
			});

			_.each(_deletedViewUrls, (b, url) => {
				store.remove(url);
				modifiedUrls.push(url);
			});

			if (modifiedUrls.length) {
				csiPrefs.notifyCustomViewStoreChanged(key, widget, modifiedUrls);
			}
		},

		/**
		 * Confirm deletion of given view
		 * @param {PreferenceView} customView
		 */
		_confirmDeleteCustomView: function(customView) {
			const store = this._getCustomViewStore();
			const url = customView.getUrl();
			const shared = customView.isShared();
			let promise;

			// if custom view is shared and already in store (not new), then query for dependents
			if (shared && store.get(url)) {
				promise = this._getCustomViewDependentUrls(url);
			} else {
				promise = Promise.resolve([]);
			}

			promise.then(dependentUrls => {
				const prefix = csiPrefs.RESOURCE_PREFIX;
				const buttonId = MessageDialog.BUTTON_DELETE;
				const resId = prefix + "ConfirmDelete" + (dependentUrls.length ? "Dependents" : "");

				const params = {
					title: locale.resource(prefix + "PromptDelete"),
					message: locale.resource(resId, [dependentUrls.length]),
					materialIcon: MessageDialog.ICON_ERROR,
					buttons: [buttonId + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL],
					onResponse: (key) => {
						this.onConfirmDeleteCustomView(key === buttonId, url, dependentUrls);
					}
				};

				// call base Controller class version as this function could be called by derived controller's confirmAction function
				Controller.prototype.confirmAction.call(this, params);
			});
		},

		/**
		 * Create new PreferenceView item with given values
		 * @param {Object} values
		 * @param {string} [name]
		 * @param {boolean} [personal]
		 * @return {PreferenceView}
		 */
		_createCustomView: function(values, name, personal) {
			const store = this._getCustomViewStore();
			const url = comm.createUrl();
			const customView = store.createItem(url, values, name, personal);

			this._newViewUrls[url] = true;

			return customView;
		},

		/**
		 * Write operations to delete custom view
		 * @param {string} url
		 */
		_deleteCustomView: function(url) {
			if (!this._isNewCustomViewUrl(url)) {
				this.m_writer.deleteNode(url);

				// only need to delete the view from the store if it is not new
				this._deletedViewUrls[url] = true;
			}

			// if view is deleted, then no need to save (this is probably not necessary since delete will be done before save, but just in case) 
			delete this._changedViews[url];
		},

		/**
		 * Return list of custom view URLs that are dependent on given custom view
		 * @param {string} url
		 * @return {string[]}
		 */
		_getCustomViewDependentUrls: async function(url) {
			return new Search()
				.byType(csiPrefs.TYPE_VIEW)
				.setLimitPath(url, "Parent:SourceView")
				.addParameter("Fmt.Node.Info", "Min")
				.addParameter("Fmt.Attr.Inc", "No")
				.addParameter("Fmt.AC.Rights", "No")
				.execute()
				.then(response => response.getResultUrls());
		},

		/**
		 * Return custom view store for current controller/view;
		 * in custom view dialog, this.store will be used as it is a new store with latest from server;
		 * in other in-view actions, store from widget will be used
		 * @return {CustomViews}
		 */
		_getCustomViewStore: function() {
			return this.store || this.widget.get("customViewStore");
		},

		/**
		 * Return url of default custom view for current context and optional party
		 * @param {string} urlParty
		 * @return {string}
		 */
		_getDefaultUrl: function(urlParty) {
			return csiPrefs.getDefaults().get(this.widget.getViewPreferenceContext(), urlParty || this._getPartyUrl());
		},

		/**
		 * Return default party for view. If the view allows only shared views, then return an empty url; otherwise return the current user url
		 * @return {string}
		 */
		_getPartyUrl: function() {
			return this.widget.prefSiteOnly ? csi.EMPTY_URL : state.get("userUrl");
		},

		/**
		 * Determine if given URL represents a deleted custom view
		 * @param {string} url
		 * @return {boolean}
		 */
		_isDeletedCustomViewUrl: function(url) {
			return this._deletedViewUrls[url];
		},

		/**
		 * Determine if given URL represents a new custom view
		 * @param {string} url
		 * @return {boolean}
		 */
		_isNewCustomViewUrl: function(url) {
			return this._newViewUrls[url];
		},

		/**
		 * Write operations to save custom view
		 * @param {PreferenceView} customView
		 */
		_saveCustomView(customView, selectedCnlIsNew) {
			const { widget, m_writer } = this;
			const { customViewContainerUrl, customViewId, customViewPathHints, defView, } = widget;
			const enableCustomViewDialog = widget.get("enableCustomViewDialog");
			const { sourceView, urlNode, values } = customView;
			const targetType = defView.getPageClass().type;
			const sourceViewCnl = sourceView?.getUrl();
			const currentViewCnl = (sourceViewCnl || !csiPrefs.hasPersonalAccess() || !enableCustomViewDialog) ? urlNode : customViewId;
			writeCustomView(m_writer, customViewContainerUrl, customViewPathHints, values, currentViewCnl, targetType, sourceViewCnl, selectedCnlIsNew);

			if (!sourceViewCnl && enableCustomViewDialog) {
				const attributeKeys = values.attributeKeys.filter(filterOutEmptyGroup);
				
				// update attributeKeys so that the column selector would refresh 
				widget.set("attributeKeys", attributeKeys);
			}

			this._changedViews[urlNode] = customView;
		},

		/**
		 * Write operations to save given custom view as default for given party.
		 * @param {string} urlCustomView
		 * @param {string} urlParty
		 */
		_writeDefault: function(urlCustomView, urlParty) {
			const defaults = csiPrefs.getDefaults();
			defaults.trackChanges();
			defaults.set(this.widget.getViewPreferenceContext(), urlCustomView, urlParty || this._getPartyUrl());
			defaults.write(this.m_writer);

			// no need to return results
			this.m_writer.noReturnNodes();
		},

		/**
		 * Remove references to custom view in user/site defaults when a custom view's changes from system-wide to role or made personal
		 * @param {PreferenceView} customView 
		 */
		_writeRemovedCustomViewDefaults: function(customView) {
			const writer = this.m_writer;
			const url = customView.urlNode;
			const party = customView.getParty();
			let q;

			q = writer.createQuery();
			q.Type("UserPreferences");
			q.RefAttr("DefaultViews", xml.OP_EQUALS, url);
			q.Not().RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, party);
			writer.changeQuery(q).changeAttribute("DefaultViews", xml.ATTR_REFLIST, [url], false, true);

			q = writer.createQuery();
			q.Type("SitePreferences");
			q.RefAttr("SiteDefaultViews", xml.OP_EQUALS, url);
			q.Not().RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, party);
			writer.changeQuery(q).changeAttribute("SiteDefaultViews", xml.ATTR_REFLIST, [url], false, true);
		}
	});
});